import { useState } from "react";
import { CreditCard, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { formatINR, fmtDateTime } from "@/lib/format";
import { Spinner } from "@/components/site/Primitives";

const loadCheckout = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = resolve;
  s.onerror = () => reject(new Error("Couldn't load the payment window. Check your connection and try again."));
  document.body.appendChild(s);
});

export const dueLabel = (q) => (q.deposit_percent < 100 ? `${q.deposit_percent}% advance` : "Full payment");

export const PaymentDone = ({ q }) => (
  <div className="border border-[#34C759]/40 bg-[#34C759]/5 p-6 flex gap-4" data-testid="quote-payment-done">
    <ShieldCheck className="text-[#34C759] shrink-0" />
    <div>
      <p className="text-white font-semibold">Payment of {formatINR(q.payment?.amount_inr)} received</p>
      <p className="text-sm text-zinc-400 mt-1">{q.payment?.method}{q.payment?.payment_id ? ` · ${q.payment.payment_id}` : ""} · {fmtDateTime(q.payment?.at)}. We'll set up your project and confirm the start time shortly.</p>
    </div>
  </div>
);

export const PayPanel = ({ token, q, onPaid }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const pay = async () => {
    setBusy(true); setErr(null);
    try {
      const { data: o } = await api.post(`/public/quotes/${token}/pay/order`);
      await loadCheckout();
      const rzp = new window.Razorpay({
        key: o.key_id, order_id: o.order_id, amount: o.amount, currency: o.currency, name: o.name, description: o.description,
        prefill: o.prefill, theme: { color: "#0055FF" },
        modal: { ondismiss: () => setBusy(false) },
        handler: async (res) => {
          try { onPaid((await api.post(`/public/quotes/${token}/pay/verify`, res)).data); }
          catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
        },
      });
      rzp.on("payment.failed", (r) => setErr(r.error?.description || "Payment failed. Please try again."));
      rzp.open();
    } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="border border-[#0055FF]/40 bg-[#0055FF]/5 p-6 md:p-8 space-y-4" data-testid="quote-pay-panel">
      <p className="text-[#34C759] text-sm" data-testid="quote-status-accepted">Accepted by {q.accepted?.name} on {fmtDateTime(q.accepted?.at)}.</p>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eb-label">{dueLabel(q)} due to start</p>
          <p className="font-display text-4xl font-black mt-1" data-testid="quote-amount-due">{formatINR(q.amount_due)}</p>
        </div>
        {q.payments_enabled && <button className="eb-btn-primary" disabled={busy} onClick={pay} data-testid="quote-pay-btn">{busy ? <Spinner /> : <CreditCard size={16} />}Pay {formatINR(q.amount_due)}</button>}
      </div>
      {!q.payments_enabled && <p className="text-sm text-zinc-400" data-testid="quote-pay-unavailable">Online payment is being set up. We'll share payment details with you directly, and your project starts once payment is received.</p>}
      {q.payments_enabled && <p className="text-xs text-zinc-500">Secure checkout by Razorpay. UPI, cards and netbanking accepted. Your project starts once payment is received.</p>}
      {err && <p className="text-sm text-[#FF3B30]" role="alert" data-testid="quote-pay-error">{err}</p>}
    </div>
  );
};
